import React, { useEffect, useRef, useState } from "react";
import TutorialButton from "./TutorialButton";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { cn } from "../lib/utils";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "./ui/tooltip";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { Avatar, AvatarFallback } from "./ui/avatar";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import {
  PiListBold,
  PiXBold,
  PiGlobeBold,
  PiSignOutBold,
  PiMonitorPlayBold,
  PiCaretRightBold,
} from "react-icons/pi";
import { useTranslation } from "react-i18next";
import { jwtDecode } from "jwt-decode";
import logo from "../assets/vertice_logoHorizontal.png";
import logoCuadrado from "../assets/Mini_Logo_Web_copy.png";
import useGlobalNotifications from "../hooks/useGlobalNotifications";
import usePendingDemos from "../hooks/usePendingDemos";
import activityTracker from "../lib/activityTracker";
import { useAccess } from "../contexts/AccessContext";
import { ALL_SIDEBAR_ITEMS } from "../constants/sidebarItems";
import axiosClient from "../api/axiosClient";
import { useSimulator } from "../contexts/SimulatorContext";
import useTheme from "../hooks/useTheme";
import { useWhatsNew } from "../hooks/useWhatsNew";
import { WhatsNewModal } from "./WhatsNewModal";

function computeInitials(payload) {
  try {
    let source =
      payload?.full_name ||
      payload?.name ||
      payload?.user_name ||
      payload?.username ||
      payload?.email ||
      payload?.user_id ||
      "";
    if (typeof source !== "string") source = String(source || "");
    if (source.includes("@")) source = source.split("@")[0];
    const parts = source
      .replace(/[^A-Za-z0-9_\-.\s]/g, " ")
      .split(/[\s._-]+/)
      .filter(Boolean);
    if (!parts.length) return "U";
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  } catch (_) {
    return "U";
  }
}

// ─── Nav Item ───────────────────────────────────────────────────────────────

function NavItem({ item, isCollapsed, activeItemClass, inactiveItemClass, counters, location, itemRef, t }) {
  const label = t(item.i18nKey || item.name, item.name);
  const badge =
    (item.path === "/chats" && location.pathname !== "/chats" && !location.pathname.startsWith("/chat/") && counters.unread_messages > 0 && counters.unread_messages) ||
    (item.path === "/contactos" && location.pathname !== "/contactos" && counters.new_contacts > 0 && counters.new_contacts) ||
    (item.path === "/documentos" && location.pathname !== "/documentos" && counters.new_documents > 0 && counters.new_documents) ||
    (item.path === "/mis-agendas" && location.pathname !== "/mis-agendas" && counters.pending_demos > 0 && counters.pending_demos) ||
    null;

  const inner = (
    <NavLink
      to={item.path}
      className={({ isActive }) =>
        cn(
          "group relative flex items-center h-12 [@media(max-height:850px)]:h-9 rounded-lg transition-all duration-200 border",
          isCollapsed ? "justify-center px-0 w-full" : "gap-3 px-4",
          isActive ? `${activeItemClass} shadow-sm` : inactiveItemClass
        )
      }
      aria-label={label}
    >
      <span className={cn("flex-shrink-0 relative", isCollapsed && "mx-auto")}>
        {item.icon}
        {badge && (
          <span className="absolute -top-2 -right-2 bg-red-500 text-white text-[10px] rounded-full h-5 min-w-[1.3rem] px-1 flex items-center justify-center font-bold">
            {badge > 99 ? "99+" : badge}
          </span>
        )}
      </span>
      {!isCollapsed && (
        <span className="text-sm font-medium truncate whitespace-nowrap">
          {label}
        </span>
      )}
    </NavLink>
  );

  if (isCollapsed) {
    return (
      <li ref={itemRef}>
        <Tooltip>
          <TooltipTrigger asChild>{inner}</TooltipTrigger>
          <TooltipContent side="right">{label}</TooltipContent>
        </Tooltip>
      </li>
    );
  }

  return <li ref={itemRef}>{inner}</li>;
}

// ─── Email Submenu Item ──────────────────────────────────────────────────────

function EmailNavItem({ item, isCollapsed, activeItemClass, inactiveItemClass, location, itemRef, openMenus, setOpenMenus, isDark, t }) {
  const isEmailActive = location.pathname.startsWith("/email/");
  const isOpen = openMenus[item.name];
  const label = t(item.i18nKey || item.name, item.name);

  if (isCollapsed) {
    return (
      <li ref={itemRef}>
        <Popover open={isOpen} onOpenChange={(v) => setOpenMenus((p) => ({ ...p, [item.name]: v }))}>
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="w-full">
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    className={cn(
                      "group relative flex items-center w-full h-12 [@media(max-height:850px)]:h-9 rounded-lg transition-all duration-200 border justify-center px-0",
                      isEmailActive ? activeItemClass : inactiveItemClass
                    )}
                    aria-label={label}
                  >
                    <span className="flex-shrink-0 mx-auto">{item.icon}</span>
                  </button>
                </PopoverTrigger>
              </span>
            </TooltipTrigger>
            <TooltipContent side="right">{label}</TooltipContent>
          </Tooltip>
          <PopoverContent side="right" align="start">
            <p className="text-[10px] uppercase tracking-wide text-gray-500 mb-1.5 px-1">Email</p>
            <ul className="flex flex-col gap-1 [@media(max-height:850px)]:gap-0.5">
              {item.children.map((child) => (
                <li key={child.name}>
                  <NavLink
                    to={child.path}
                    onClick={() => setOpenMenus((p) => ({ ...p, [item.name]: false }))}
                    className={({ isActive }) =>
                      cn(
                        "flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors",
                        isActive
                          ? isDark ? "bg-[#dc2626] text-white font-semibold" : "bg-[rgba(220,38,38,0.09)] text-[#dc2626] font-semibold"
                          : "text-gray-500 dark:text-gray-400 hover:bg-[var(--sidebar-hover-bg)] hover:text-gray-700 dark:hover:text-gray-200"
                      )
                    }
                  >
                    {child.icon && <span className="shrink-0">{child.icon}</span>}
                    <span className="font-medium">{child.name}</span>
                  </NavLink>
                </li>
              ))}
            </ul>
          </PopoverContent>
        </Popover>
      </li>
    );
  }

  return (
    <li ref={itemRef}>
      <button
        type="button"
        onClick={() => setOpenMenus((p) => ({ ...p, [item.name]: !p[item.name] }))}
        className={cn(
          "group relative flex items-center w-full h-12 [@media(max-height:850px)]:h-9 rounded-lg transition-all duration-200 border gap-3 px-4",
          isEmailActive ? activeItemClass : inactiveItemClass
        )}
      >
        <span className="flex-shrink-0">{item.icon}</span>
        <span className="text-sm font-medium truncate whitespace-nowrap flex-1 text-left">
          {label}
        </span>
        <PiCaretRightBold
          size={14}
          className={cn("shrink-0 transition-transform duration-200", isOpen && "rotate-90")}
        />
      </button>
      {isOpen && (
        <div className="ml-6 mt-1 flex flex-col gap-1 bg-[var(--sidebar-item-bg)] border border-[var(--sidebar-border)] rounded-xl p-2 shadow-[0_4px_16px_rgba(0,0,0,0.08)] dark:shadow-[0_4px_16px_rgba(0,0,0,0.4)]">
          <div className="flex items-center justify-between mb-1 px-1">
            <span className="text-xs uppercase tracking-wide text-gray-500 select-none">Email</span>
            <button
              onClick={() => setOpenMenus((p) => ({ ...p, [item.name]: false }))}
              className="text-gray-400 hover:text-gray-600 p-1 rounded transition-colors"
            >
              <PiXBold size={12} />
            </button>
          </div>
          <ul className="flex flex-col gap-1 [@media(max-height:850px)]:gap-0.5">
            {item.children.map((child) => (
              <li key={child.name}>
                <NavLink
                  to={child.path}
                  className={({ isActive }) =>
                    cn(
                      "flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors",
                      isActive
                        ? isDark ? "bg-[#dc2626] text-white font-semibold" : "bg-[rgba(220,38,38,0.09)] text-[#dc2626] font-semibold"
                        : "text-gray-500 dark:text-gray-400 hover:bg-[var(--sidebar-hover-bg)] hover:text-gray-700 dark:hover:text-gray-200"
                    )
                  }
                >
                  {child.icon && <span className="shrink-0">{child.icon}</span>}
                  <span className="font-medium">{child.name}</span>
                </NavLink>
              </li>
            ))}
          </ul>
        </div>
      )}
    </li>
  );
}

// ─── Sidebar Core ────────────────────────────────────────────────────────────

function SidebarCore({ isCollapsed, setCollapsed, isMobile, onClose }) {
  const { i18n, t } = useTranslation();
  const { canAccess, loading: accessLoading } = useAccess();
  const [openMenus, setOpenMenus] = useState({});
  const location = useLocation();
  const navigate = useNavigate();
  const { counters } = useGlobalNotifications();
  const { count: pendingDemosCount } = usePendingDemos();
  const [menuItems, setMenuItems] = useState([]);
  const [initials, setInitials] = useState("U");
  const [userData, setUserData] = useState({ fullName: "", email: "" });
  const [isVendedor, setIsVendedor] = useState(false);
  const { isActive: simulatorActive, activate: activateSimulator, deactivate: deactivateSimulator } = useSimulator();
  const { isDark } = useTheme();
  const navRef = useRef(null);
  const itemRefs = useRef([]);
  const [indicatorTop, setIndicatorTop] = useState(0);
  const [indicatorHeight, setIndicatorHeight] = useState(48);
  const firstLoadRef = useRef(true);
  const whatsNew = useWhatsNew();

  const effectivelyOpen = isMobile ? true : !isCollapsed;

  const changeLanguage = async (lng) => {
    try {
      i18n.changeLanguage(lng);
      await axiosClient.post("/ventas/settings/access", { language: lng });
    } catch (err) {
      console.error("[Sidebar] Error changing language", err);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) return;
    try {
      const payload = jwtDecode(token);
      setInitials(computeInitials(payload));
      setUserData({
        fullName: payload?.full_name || payload?.name || payload?.user_name || payload?.username || "Usuario",
        email: payload?.email || payload?.username || "",
      });
      setIsVendedor(Number(payload?.level ?? 0) <= 1);
    } catch (err) {
      console.warn("Sidebar token decode failed", err?.message);
    }
  }, []);

  useEffect(() => {
    if (!accessLoading) {
      setMenuItems(ALL_SIDEBAR_ITEMS.filter((item) => canAccess(item.id)));
    }
  }, [accessLoading, location.pathname, canAccess]);

  useEffect(() => {
    if (!navRef.current || menuItems.length === 0) return;
    const pathname = decodeURIComponent(location.pathname);
    const activeIndex = menuItems.findIndex((item) => {
      if (item.children) return pathname.startsWith("/email/");
      return (
        pathname === item.path ||
        (item.path === "/chats" && pathname.startsWith("/chat/")) ||
        (item.path === "/contactos-listas" &&
          (pathname.startsWith("/contactos-listas") || pathname.startsWith("/contacto/")))
      );
    });
    if (activeIndex !== -1 && itemRefs.current[activeIndex]) {
      setIndicatorTop(itemRefs.current[activeIndex].offsetTop);
      setIndicatorHeight(itemRefs.current[activeIndex].offsetHeight);
    }
  }, [location.pathname, menuItems, isCollapsed]);

  // Scroll-lock: prevent body scroll while the mobile drawer is open
  useEffect(() => {
    document.body.style.overflow = isMobile ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [isMobile]);

  useEffect(() => {
    if (firstLoadRef.current) { firstLoadRef.current = false; return; }
    if (isMobile) { if (onClose) onClose(); }
    else { setCollapsed(true); }
    if (location.pathname.startsWith("/email/")) {
      setOpenMenus((prev) => ({ ...prev, Email: true }));
    } else {
      setOpenMenus((prev) => ({ ...prev, Email: false }));
    }
  }, [location.pathname]);

  const activeItemClass = isDark
    ? "sidebar-active-dark text-white border-[#b91c1c]"
    : "sidebar-active-soft text-[#dc2626] border-[rgba(220,38,38,0.2)]";
  const inactiveItemClass =
    "border-transparent text-gray-500 dark:text-gray-400 hover:bg-[var(--sidebar-hover-bg)] hover:text-gray-700 dark:hover:text-gray-200 hover:border-[var(--sidebar-border)]";

  return (
    <TooltipProvider delayDuration={200}>
      <div
        className={cn(
          "h-full bg-[var(--sidebar-bg)] text-gray-700 dark:text-gray-300 shadow-[1px_0_0_0_var(--sidebar-border)] flex flex-col transition-all duration-300 relative border-r border-[var(--sidebar-border)] overflow-x-hidden shrink-0 flex-none",
          effectivelyOpen ? "w-56" : "w-20",
          isMobile && "h-full w-64 border-r-0"
        )}
      >
        {/* Logo */}
        <div className="flex items-center justify-center py-3 [@media(max-height:850px)]:py-1.5 border-b border-[var(--sidebar-border)]">
          <div
            className={cn(
              "rounded-xl shadow-sm bg-[var(--sidebar-item-bg)] border border-[var(--sidebar-border)] flex items-center justify-center overflow-hidden",
              effectivelyOpen ? "h-12 w-12" : "h-10 w-10"
            )}
          >
            <img src={logoCuadrado} alt="Logo" className="h-full w-full object-contain" />
          </div>
        </div>

        {/* Burger */}
        <div
          className={cn(
            "flex items-center border-b border-[var(--sidebar-border)] py-4 [@media(max-height:850px)]:py-2",
            effectivelyOpen ? "justify-between px-4" : "justify-center px-0"
          )}
        >
          <button
            onClick={() => (isMobile ? onClose() : setCollapsed((o) => !o))}
            className="focus:outline-none p-1.5 rounded-md hover:bg-[var(--sidebar-hover-bg)] text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 transition-colors"
            aria-label="Expandir/cerrar menú"
          >
            {isMobile ? <PiXBold size={22} /> : <PiListBold size={22} />}
          </button>
          {effectivelyOpen && (
            <img src={logo} alt="VerticeTrade Logo" className="pl-4 h-8 w-auto" />
          )}
        </div>

        {/* Nav */}
        <nav ref={navRef} className="flex-1 overflow-y-auto overflow-x-hidden mt-2 [@media(max-height:850px)]:mt-1 relative">
          <ul className="flex flex-col gap-1 px-2 [@media(max-height:850px)]:gap-0.5">
            {menuItems.map((item, index) =>
              item.children ? (
                <EmailNavItem
                  key={item.name}
                  item={item}
                  isCollapsed={!effectivelyOpen}
                  activeItemClass={activeItemClass}
                  inactiveItemClass={inactiveItemClass}
                  location={location}
                  itemRef={(el) => (itemRefs.current[index] = el)}
                  openMenus={openMenus}
                  setOpenMenus={setOpenMenus}
                  isDark={isDark}
                  t={t}
                />
              ) : (
                <NavItem
                  key={item.name}
                  item={item}
                  isCollapsed={!effectivelyOpen}
                  activeItemClass={activeItemClass}
                  inactiveItemClass={inactiveItemClass}
                  counters={{ ...counters, pending_demos: pendingDemosCount }}
                  location={location}
                  itemRef={(el) => (itemRefs.current[index] = el)}
                  t={t}
                />
              )
            )}
          </ul>
          {/* Indicador animado */}
          {!isMobile && (
            <div
              className="sidebar-indicator absolute left-0 top-0 w-[3px] transition-all duration-300 ease-in-out z-10"
              style={{ transform: `translateY(${indicatorTop}px)`, height: `${indicatorHeight}px` }}
            />
          )}
        </nav>

        {/* Footer */}
        <div className="mt-auto border-t border-[var(--sidebar-border)] p-3 [@media(max-height:850px)]:p-1.5 flex flex-col gap-1">
          {/* Simulador */}
          {isVendedor && (
            <button
              onClick={simulatorActive ? deactivateSimulator : activateSimulator}
              className={cn(
                "flex items-center gap-2 w-full px-3 py-2 rounded-lg text-sm font-medium border transition-colors mb-1",
                simulatorActive
                  ? "bg-purple-100 text-purple-700 border-purple-300 hover:bg-purple-200"
                  : "text-gray-500 dark:text-gray-400 border-transparent hover:bg-[var(--sidebar-hover-bg)] hover:border-[var(--sidebar-border)]",
                !effectivelyOpen && "justify-center px-0"
              )}
              title={simulatorActive ? "Salir del Simulador" : "Simulador (ConextCRM)"}
            >
              <PiMonitorPlayBold size={16} className="shrink-0" />
              {effectivelyOpen && (
                <span>{simulatorActive ? "Salir del Simulador" : "Simulador (ConextCRM)"}</span>
              )}
            </button>
          )}

          <TutorialButton section="intro" label="¿Qué es ConextCRM?" sidebarOpen={effectivelyOpen} />

          {/* User Menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className={cn(
                  "flex items-center gap-3 w-full p-2 rounded-lg transition-colors hover:bg-[var(--sidebar-hover-bg)] border border-transparent hover:border-[var(--sidebar-border)] focus:outline-none",
                  !effectivelyOpen && "justify-center px-0"
                )}
                aria-label="Menú de usuario"
              >
                <Avatar>
                  <AvatarFallback>{initials}</AvatarFallback>
                </Avatar>
                {effectivelyOpen && (
                  <div className="flex flex-col text-left overflow-hidden">
                    <span className="text-sm font-semibold truncate dark:text-gray-100">{userData.fullName}</span>
                    <span className="text-[10px] text-gray-500 dark:text-gray-400 truncate">{userData.email}</span>
                  </div>
                )}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent side="right" align="end">
              <DropdownMenuLabel>
                <PiGlobeBold size={13} />
                {t("common.language", "Idioma")}
              </DropdownMenuLabel>
              <DropdownMenuItem
                onClick={() => changeLanguage("es")}
                className={cn(
                  i18n.language === "es" && "bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 font-semibold"
                )}
              >
                <span className="w-4 text-xs">🇪🇸</span> Español
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => changeLanguage("en")}
                className={cn(
                  i18n.language === "en" && "bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 font-semibold"
                )}
              >
                <span className="w-4 text-xs">🇺🇸</span> English
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => changeLanguage("va")}
                className={cn(
                  i18n.language === "va" && "bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 font-semibold"
                )}
              >
                <span className="w-4 text-xs">VA</span> Valencià
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={async () => {
                  try { await activityTracker.shutdown(); } catch (_) {}
                  try {
                    const rt = localStorage.getItem("refresh_token");
                    if (rt) await axiosClient.post("/auth/logout", { refresh_token: rt });
                  } catch (_) { /* logout best-effort */ }
                  localStorage.removeItem("token");
                  localStorage.removeItem("refresh_token");
                  navigate("/login");
                }}
                className="text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
              >
                <PiSignOutBold size={15} />
                {t("common.logout", "Cerrar sesión")}
              </DropdownMenuItem>
              {whatsNew.enabled && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => whatsNew.openModal()}
                    className="text-[11px] text-gray-400 flex items-center justify-between"
                  >
                    <span>Versión {whatsNew.version}</span>
                    <span className="flex items-center gap-1">
                      Novedades
                      {whatsNew.hasUnseen && (
                        <span className="w-1.5 h-1.5 rounded-full bg-red-500" />
                      )}
                    </span>
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>

          {whatsNew.enabled && (
            <WhatsNewModal
              open={whatsNew.open}
              onClose={whatsNew.closeModal}
              newVersions={whatsNew.newVersions}
            />
          )}
        </div>
      </div>
    </TooltipProvider>
  );
}

// ─── Sidebar Export ──────────────────────────────────────────────────────────

const Sidebar = ({ isMobile = false, isOpen = false, onClose }) => {
  const [collapsed, setCollapsed] = useState(true);

  if (isMobile) {
    return (
      <>
        {isOpen && (
          <div
            className="fixed inset-0 z-[999] bg-black/50"
            onClick={onClose}
            aria-hidden="true"
          />
        )}
        <div
          className={`fixed inset-y-0 left-0 z-[1000] h-full w-64 shadow-2xl transition-transform duration-300 ${
            isOpen ? "translate-x-0" : "-translate-x-full"
          }`}
        >
          <SidebarCore isCollapsed={false} setCollapsed={() => {}} isMobile={true} onClose={onClose} />
        </div>
      </>
    );
  }

  return (
    <SidebarCore
      isCollapsed={collapsed}
      setCollapsed={setCollapsed}
      isMobile={false}
      onClose={null}
    />
  );
};

export default Sidebar;
